import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Text, Button, FlexboxGrid } from 'rsuite';
import {
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressRegistrationVerbiage,
  selectConfigExpressTaxEnabled,
  selectConfigExpressTaxCertificateEnabled,
} from '../../../store/slices/config/configSelectors';
import pluralize from 'pluralize';
import UsersLicenses from './UsersLicenses';
import { setCurrentStep } from '../../../store/slices/flow/flowSlice';
import {
  mapApplyFlowConfigToFlowSteps,
  mapTaxCertificateFlowConfigToFlowSteps,
  setTaxSteps,
} from '../../../store/slices/flow/flowThunks';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import { C_ACTIVE_PERMIT_STATUSES } from '../../../common/consts';
import { useAppDispatch } from 'src/store/store';
import { License, LicenseType } from 'src/types/licenses';
import IconIndicator from 'src/components/user-feedback/IconIndicator';
import { useResize } from 'src/hooks/useResize';

interface UsersLicensesWrapperProps {
  licenses: License[];
  loading?: boolean;
  errorMessage?: string;
  contentSectionHeight: number;
}

const UsersLicensesWrapper = ({
  licenses,
  loading,
  errorMessage,
  contentSectionHeight,
}: UsersLicensesWrapperProps) => {
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();
  const { isMobileOrTablet } = useResize();

  const taxEnabled = useSelector(selectConfigExpressTaxEnabled);
  const taxCertificateEnabled = useSelector(
    selectConfigExpressTaxCertificateEnabled
  );
  const registrationVerbiage = useSelector(
    selectConfigExpressRegistrationVerbiage
  );
  const typeVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);

  const activeLicenseCounts = useMemo(() => {
    if (!licenses) return null;
    const active = licenses.filter((license: License) =>
      C_ACTIVE_PERMIT_STATUSES.includes(license.status)
    );
    const taxCertCount = active.filter(
      (l) => l.license_type === LicenseType.TaxCertificate
    ).length;
    const permitCount = active.length - taxCertCount;
    return { permitCount, taxCertCount };
  }, [licenses]);

  const onFlowStepsMapped = useCallback(
    (path?: string) => {
      dispatch(setCurrentStep(1));
      placeNavigate(`/${path}`);
    },
    [dispatch, placeNavigate]
  );

  const onApplyForNewLicense = useCallback(() => {
    dispatch(mapApplyFlowConfigToFlowSteps(onFlowStepsMapped));
  }, [dispatch, onFlowStepsMapped]);

  const onInitiateTax = useCallback(() => {
    dispatch(setTaxSteps(onFlowStepsMapped));
  }, [dispatch, onFlowStepsMapped]);

  const onInitiateTaxCertificate = useCallback(() => {
    dispatch(mapTaxCertificateFlowConfigToFlowSteps(onFlowStepsMapped));
  }, [dispatch, onFlowStepsMapped]);

  const buttonItemStyle: React.CSSProperties = {
    flex: '1 1 calc(50% - 4px)',
    display: 'flex',
  };

  return (
    <FlexboxGrid
      role="grid"
      className="dashboard-licenses-panel"
    >
      <FlexboxGrid.Item
        role="row"
        colspan={24}
        className="mb-8"
      >
        <div role="cell">
          <Text
            as="h2"
            className="text-large text-bold"
          >{`Active ${pluralize(registrationVerbiage || '')}`}</Text>
        </div>
      </FlexboxGrid.Item>

      <FlexboxGrid.Item
        role="row"
        colspan={24}
        className="mb-8"
        style={{ height: 24, overflow: 'hidden' }}
      >
        <div role="cell">
          {activeLicenseCounts && (
            <Text className="text-medium text-semibold dashboard-licenses-panel__counts">
              {`${pluralize(typeVerbiage, activeLicenseCounts.permitCount)} (${activeLicenseCounts.permitCount})`}
              {activeLicenseCounts.taxCertCount > 0 &&
                ` • ${pluralize('Tax Certificate', activeLicenseCounts.taxCertCount)} (${activeLicenseCounts.taxCertCount})`}
            </Text>
          )}
        </div>
      </FlexboxGrid.Item>

      <FlexboxGrid.Item
        role="row"
        colspan={24}
        className="content-section dashboard-licenses-panel__content"
        style={
          isMobileOrTablet
            ? undefined
            : {
                flex: 1,
                minHeight: contentSectionHeight,
                display: 'flex',
                flexDirection: 'column',
              }
        }
      >
        {loading ? (
          <div className="pv-48">
            <IconIndicator />
          </div>
        ) : (
          <div
            role="cell"
            style={
              isMobileOrTablet
                ? undefined
                : {
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                  }
            }
          >
            <div style={isMobileOrTablet ? undefined : { flex: 1 }}>
              {licenses.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => placeNavigate('/all-permits')}
                    data-cy="see-all-licenses-header-link"
                    className="link-color mb-8"
                  >
                    See All
                  </Button>
                </div>
              )}
              <UsersLicenses
                licenses={licenses}
                errorMessage={errorMessage}
                taxCertificateEnabled={taxCertificateEnabled}
              />
            </div>

            <div style={{ flexShrink: 0 }}>
              {taxEnabled && licenses.length > 0 && (
                <Button
                  className="btn-primary mb-8 mt-16"
                  style={{ width: '100%' }}
                  onClick={onInitiateTax}
                  data-cy="pay-tax-button"
                >
                  Pay Tax
                </Button>
              )}

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  alignItems: 'stretch',
                }}
              >
                <div style={buttonItemStyle}>
                  <Button
                    className="btn-secondary"
                    style={{ width: '100%' }}
                    onClick={onApplyForNewLicense}
                    data-cy="apply-for-new-license-button"
                  >
                    Apply for new {typeVerbiage}
                  </Button>
                </div>

                {licenses.length > 0 && (
                  <div style={buttonItemStyle}>
                    <Button
                      className="btn-secondary"
                      style={{ width: '100%' }}
                      onClick={() => placeNavigate('/all-permits')}
                      data-cy="see-all-licenses-button"
                    >
                      See all {pluralize(typeVerbiage)}
                    </Button>
                  </div>
                )}

                {taxCertificateEnabled && (
                  <div style={buttonItemStyle}>
                    <Button
                      className="btn-secondary"
                      style={{ width: '100%' }}
                      onClick={onInitiateTaxCertificate}
                      data-cy="tax-certificate-button"
                    >
                      Apply for Tax Certificate
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default UsersLicensesWrapper;
